import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';
import { Phone, ArrowRight, BookOpen, Activity } from 'lucide-react';

const SpondylitisCondition = () => {
  const [openFaqId, setOpenFaqId] = useState(null);

  const toggleFaq = (id) => {
    setOpenFaqId(openFaqId === id ? null : id);
  };

  const pageData = {
    title: "Ankylosing Spondylitis in New York City",
    metaDescription: "Specialized care for ankylosing spondylitis in New York. Learn about treatments to manage inflammation, improve posture, and preserve mobility at NY Center for Spinal Disorders.",
    metaKeywords: "Ankylosing Spondylitis, inflammatory spine condition, arthritis treatment NYC, Dr. Shah, Dr. Tindel",
    heroDescription: "Comprehensive treatment for ankylosing spondylitis with a focus on slowing progression and maintaining mobility.",
    forWho: [
      "Ankylosing spondylitis (AS) is a type of inflammatory arthritis that primarily affects the spine, causing painful swelling in the joints between vertebrae and sometimes in other joints (like hips). Over time, chronic inflammation can lead to new bone growth, fusing sections of the spine together and reducing flexibility. This can result in a forward-stooped posture and stiffness that’s particularly noticeable in the morning or after rest.",
      "While the exact cause is unclear, genetics play a significant role; many people with AS carry a specific gene called HLA-B27. The condition often starts in young adults, typically causing persistent back pain and stiffness that improves with exercise but not with rest."
    ],
    conditionInfo: [
      "If you have lingering back pain that improves with activity and worsens at rest—especially if you’re a younger adult—it’s important to investigate ankylosing spondylitis. Our spine specialists in Manhattan can provide imaging and blood tests to confirm a diagnosis, then guide you toward strategies to manage inflammation and maintain spinal mobility.",
    ],
    treatment: [
      "Medications (such as anti-inflammatories or biologics) help manage pain and slow progression.",
      "Exercise, stretching, and physical therapy are key to maintaining flexibility.",
      "Surgery is less common but may help if severe deformities or complications develop."
    ],
    postCare: [
      "Regular check-ups ensure inflammation remains controlled.",
      "Supportive resources, like patient education and online portals, keep you informed and engaged in self-care."
    ],
    nonSurgicalOptions: [
      "Physical therapy",
      "Anti-inflammatory medications",
      "Lifestyle modifications (ergonomic adjustments, posture training)"
    ],
    faqs: [
      {
        id: 1,
        question: "Will I need medication forever?",
        answer: "Treatment duration varies, but consistent management often prevents severe symptoms."
      },
      {
        id: 2,
        question: "Can exercise help?",
        answer: "Yes. Low-impact workouts and targeted stretches improve posture and mobility."
      }
    ]
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <Helmet>
        <title>{pageData.title} - NYC for Spinal Disorders</title>
        <meta name="description" content={pageData.metaDescription} />
        <meta name="keywords" content={pageData.metaKeywords} />
        <meta name="author" content="New York Center for Spinal Disorders" />
        <meta property="og:title" content={pageData.title} />
        <meta property="og:description" content={pageData.metaDescription} />
        <meta property="og:image" content="/images/logo-header.png" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Helmet>
      
      <Header />

      
      <div className="flex items-center relative min-h-[40vh] bg-gradient-to-r from-[#003666] to-[#0062B1] py-8">
        <div className="absolute inset-0">
          <img
            src="../images/circles.jpg"
            alt={pageData.title}
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="relative h-full max-w-6xl mx-auto px-4 flex items-center">
          <div>
            <h1 className="text-5xl font-bold text-white mb-4">{pageData.title}</h1>
            <p className="text-2xl text-blue-100">{pageData.heroDescription}</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-lg">
        <div className="max-w-6xl mx-auto py-6 px-4">
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center px-6 py-3 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition">
              Schedule Consultation <ArrowRight className="ml-2 w-4 h-4" />
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center px-6 py-3 border-2 border-blue-900 text-blue-900 rounded-full hover:bg-blue-50 transition">
              <Phone className="mr-2 w-4 h-4" /> Call Now
            </a>
          </div>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <BookOpen className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">Condition Overview</h2>
                {pageData.forWho.map((paragraph, index) => (
                  <p key={index} className="text-gray-700 leading-relaxed mb-4">{paragraph}</p>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <div className="max-w-4xl mx-auto">
            <div className="flex items-start gap-4">
              <Activity className="w-8 h-8 text-blue-600 flex-shrink-0" />
              <div>
                <h2 className="text-2xl font-bold text-blue-900 mb-4">What Patients Need to Know</h2>
                <ul className="space-y-4 text-gray-700">
                  {pageData.conditionInfo.map((info, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-blue-600 font-bold">•</span>
                      <span>{info}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>





        <div className="bg-gradient-to-r from-[#003666] to-[#0062B1] rounded-xl shadow-lg p-12 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">Patient-Centered Approach</h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Our dedicated team of board-certified orthopedic spine physicians at New York Center for Spinal Disorders works together to guide you every step of the way, always prioritizing your comfort and well-being.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 bg-white text-blue-900 rounded-full font-bold hover:bg-blue-100 transition duration-300">
              Schedule Consultation
            </Link>
            <a href="tel:+12122493840" className="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white rounded-full font-bold hover:bg-white/10 transition duration-300">
              <Phone className="w-5 h-5 mr-2" />
              Call Now
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default SpondylitisCondition;